import { useEffect, useState } from 'react';
import {
  AlertMessage,
  Theme,
  IconStyle,
  useTranslation,
  Input,
  InputTypes,
  LinkTarget,
  TextLinkStyle,
  useSession,
  CardRadioButton,
  MLPVersion,
  FAIcon,
  Checkbox,
  TextLink,
  TextLinkUnderlineStyle,
  InputValidations,
  useMasterData,
} from '@nextgen-web-framework/all';
import { UpdateCustomizeSharecodeResponse, sharebarApi } from '@commerce-webcomponents/sdk';
import { Config, AccountType, RegistrationType } from '@com.amway.commerce/webshop-configs-v1-prod/Schema';
import { createSharecode, getSharePath } from '../../utils/sharebar/sharebarHelpers';
import SocialLinks from '../SocialLinks/SocialLinks';
import { SharecodeDetails, ShareModalProps } from './types/propTypes';
import styles from './ShareModal.module.scss';

const ShareModal = ({
  showDescription = true,
  hasError,
  socialIconsVisibility,
  isCustomUrl,
  linkProps,
  shareTextMessage,
  shareDescriptionContent,
}: ShareModalProps) => {
  const t = useTranslation();
  const { isABO } = useSession();
  const [regError, setRegError] = useState(false);
  const [regChecked, setRegChecked] = useState(false);
  const [showError, setShowError] = useState(false);
  const webShopConfigs = useMasterData<Config>('WebshopConfigs') as Config;
  const Users = webShopConfigs?.shareRegistrationData;
  const regTypeConfig = webShopConfigs?.shareRegistrationConfig;
  const registrationType = regTypeConfig[linkProps?.category ?? ''];
  const mandatoryRegistrationType = registrationType === RegistrationType.REQUIRED;
  const [regType, setRegType] = useState<AccountType | undefined>(
    mandatoryRegistrationType ? Users?.[0]?.type : undefined,
  );
  const [currentSharecodeDetails, setCurrentSharecodeDetails] = useState<SharecodeDetails>();
  const [customizeSection, setCustomizeSection] = useState({
    showInput: false,
    inputText: '',
    error: false,
    customSharecodeValidated: false,
  });

  const getRegistrationContent = () => (
    <>
      {Users?.map((user) => {
        const isActive = regType === user.type;
        return (
          <CardRadioButton
            mlpVersion={MLPVersion.ONE}
            label={
              <span className={isActive ? styles['sharebar-radio-title-checked'] : 'sharebar-radio-title'}>
                {t(user.labelKey)}
              </span>
            }
            theme={isActive ? Theme.PURPLE : Theme.BLACK}
            checked={isActive}
            onChange={() => {
              setRegType(user.type);
              setRegError(false);
              if (isCustomUrl) {
                createSharebar();
              }
            }}
            key={user.type}
          />
        );
      })}
    </>
  );
  const createCustomSharecode = async () => {
    try {
      const customPayloadRequest = {
        sharecodeid: currentSharecodeDetails?.sharecodeid,
        sharecode: currentSharecodeDetails?.sharecode,
        customsharecode: customizeSection.inputText,
      };
      const res: UpdateCustomizeSharecodeResponse = await sharebarApi.updateCustomSharecode({
        payload: {
          ...customPayloadRequest,
        },
      });
      if ('sharecode' in res) {
        setCurrentSharecodeDetails({ sharecode: res.sharecode, sharecodeid: res.sharecodeid });
        setCustomizeSection({ ...customizeSection, customSharecodeValidated: true, error: false });
      }
    } catch (error) {
      setCustomizeSection({ ...customizeSection, customSharecodeValidated: true, error: true });
    }
  };
  const fetchCustomizeContent = () => {
    const shareLinkUrl = getSharePath(
      undefined,
      socialIconsVisibility.SHARELINK_DOMAIN,
      socialIconsVisibility.SHARELINK_PATH,
    );
    const CUSTOM_URL_MAX_LENGTH = 20;
    const getCustomMessage = () => {
      let customMsg = '';
      let inputStatus = InputValidations.DEFAULT;
      if (customizeSection.customSharecodeValidated && !customizeSection.error) {
        customMsg = t('web.share.url.available.msg');
        inputStatus = InputValidations.VALID;
      } else if (customizeSection.error) {
        customMsg = t('web.share.url.not.available.msg');
        inputStatus = InputValidations.INVALID;
      }
      return { customMsg, inputStatus };
    };
    const handleCustomInputBlur = (e: React.FocusEvent) => {
      if (customizeSection.inputText && e.relatedTarget?.id !== 'btn_cancel') {
        createCustomSharecode();
      }
    };
    const { customMsg, inputStatus } = getCustomMessage();
    return (
      <div className={styles['sharebar-custom-url-wrapper']}>
        <div className={`${styles['sharebar-customize-url']}`}>
          <span>{`${shareLinkUrl}/${
            customizeSection.inputText === '' ||
            customizeSection.error !== null ||
            customizeSection.customSharecodeValidated === false
              ? `${currentSharecodeDetails?.sharecode ?? ''}`
              : customizeSection.inputText
          }`}</span>
        </div>
        {/* eslint-disable jsx-a11y/no-autofocus */}

        {customizeSection.showInput ? (
          <div className={styles['custom-input-wrapper']}>
            <Input
              type={InputTypes.TEXT}
              data-testid="custom-input-box"
              autoFocus={true}
              name="customBox"
              label={t('web.share.customize.back.half')}
              value={customizeSection.inputText}
              message={customMsg}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCustomizeSection({ ...customizeSection, inputText: e.target.value })
              }
              onBlur={handleCustomInputBlur}
              status={inputStatus}
              mlpVersion={MLPVersion.TWO}
              maxLength={CUSTOM_URL_MAX_LENGTH}
              ctaTextLink={{
                id: 'btn_cancel',
                text: 'Cancel',
                onClick: () =>
                  setCustomizeSection({
                    showInput: false,
                    inputText: '',
                    error: false,
                    customSharecodeValidated: false,
                  }),
                target: LinkTarget.SELF,
              }}
            />
          </div>
        ) : (
          <TextLink
            textLinkStyle={TextLinkStyle.ICON}
            text="Customize URL"
            theme={Theme.PURPLE}
            onClick={() => setCustomizeSection({ ...customizeSection, showInput: true })}
            underline={TextLinkUnderlineStyle.NOHOVER}
            leftIcon={{ iconName: 'pen', iconStyle: IconStyle.REGULAR }}
          />
        )}
      </div>
    );
  };
  const userSelected = regChecked || mandatoryRegistrationType;
  const createSharebar = async () => {
    const sharecode = await createSharecode({ linkProps, regType });
    if (!sharecode) {
      setShowError(true);
    }
    setCurrentSharecodeDetails({ sharecode: sharecode?.sharecode, sharecodeid: sharecode?.sharecodeid });
  };
  const socialLinkHandler = () => {
    if (regChecked && !regType) {
      setRegError(true);
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (isCustomUrl) {
      // Create sharecode in case of custom url to show it to the user
      createSharebar();
    }
  }, []);

  return (
    <>
      {(hasError || showError) && (
        <div className={`${styles['alert-msg-padding']}`} data-testid="alert-error-msg">
          <AlertMessage
            className={`${styles['alert-msg-padding']}`}
            content={{
              detail: t('web.share.error.heading'),
              heading: t('web.share.error.detail'),
            }}
            hasIcon
            icon="square-exclamation"
            iconColor={Theme.ERROR}
            iconStyle={IconStyle.REGULAR}
            theme={Theme.ERROR}
          />{' '}
        </div>
      )}
      {showDescription ? (
        <div className={`${styles['sharebar-sub-text']}`}>
          <span>{shareDescriptionContent ?? (isABO() ? t('web.share.abo.desc') : t('web.share.non.abo.desc'))}</span>
        </div>
      ) : (
        ''
      )}
      {(registrationType === 'OPTIONAL' || userSelected) && (
        <div className={styles['reg-details-wrapper']}>
          {registrationType === 'OPTIONAL' && (
            <Checkbox
              name="checkbox"
              checked={regChecked}
              text={<span className={styles['checkbox-text-wrapper']}>{t('web.share.reg.type')}</span>}
              onClick={(event) => {
                const checkbox = event.target as HTMLInputElement;
                setRegChecked(!regChecked);
                setRegType(checkbox.checked ? Users[0]?.type : undefined);
              }}
            />
          )}

          {userSelected && getRegistrationContent()}
          {regError && (
            <div className={`${styles['sharebar-radio-error']}`}>
              <FAIcon iconName="triangle-exclamation" theme={Theme.RED} />
              <span className={`${styles['sharebar-sub-error']}`} data-testid="registration-error-message">
                {t('web.share.reg.type.message')}
              </span>
            </div>
          )}
        </div>
      )}
      {isCustomUrl && fetchCustomizeContent()}
      <SocialLinks
        socialLinkHandler={socialLinkHandler}
        socialIconsVisibility={socialIconsVisibility}
        regType={regType}
        existingSharecode={currentSharecodeDetails?.sharecode}
        linkProps={linkProps}
        shareTextMessage={shareTextMessage}
      />
    </>
  );
};

export default ShareModal;
